// Utilidad para manejo centralizado de errores
export const handleApiError = (error) => {
  console.error('Error de API:', error);
  
  // Error de red o timeout
  if (!error.response) {
    if (error.code === 'ECONNABORTED') {
      return {
        message: 'Tiempo de espera agotado. Verifica tu conexión a internet.',
        type: 'timeout'
      };
    }
    return {
      message: 'No se puede conectar al servidor. Verifica tu conexión a internet.',
      type: 'network'
    };
  }
  
  // Errores del servidor
  const { status, data } = error.response;
  
  switch (status) {
    case 400:
      return {
        message: data?.error || data?.message || 'Datos inválidos',
        type: 'validation',
        details: data
      };
    case 401:
      return {
        message: 'Credenciales inválidas o sesión expirada',
        type: 'auth'
      };
    case 403:
      return {
        message: 'No tienes permisos para realizar esta acción',
        type: 'permission'
      };
    case 404:
      return {
        message: 'Recurso no encontrado',
        type: 'not_found'
      };
    case 500:
      return {
        message: 'Error interno del servidor. Inténtalo más tarde.',
        type: 'server'
      };
    default:
      return {
        message: data?.error || data?.message || 'Error inesperado',
        type: 'unknown',
        details: data
      };
  }
};

// Función para reintentar peticiones fallidas con lógica mejorada
export const retryRequest = async (requestFunction, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Intento ${attempt} de ${maxRetries}...`);
      return await requestFunction();
    } catch (error) {
      lastError = error;
      const errorInfo = handleApiError(error);
      console.log(`Intento ${attempt} falló:`, errorInfo.message);
      
      // Si es el último intento, lanzar el error
      if (attempt === maxRetries) {
        console.error(`Todos los ${maxRetries} intentos fallaron. Error final:`, errorInfo.message);
        throw error;
      }
      
      // Solo reintentar en ciertos tipos de error
      if (errorInfo.type === 'network' || errorInfo.type === 'timeout' || errorInfo.type === 'server') {
        const nextDelay = delay * Math.pow(2, attempt - 1); // Backoff exponencial
        console.log(`Esperando ${nextDelay}ms antes del siguiente intento...`);
        await new Promise(resolve => setTimeout(resolve, nextDelay));
      } else {
        // Para errores de validación o auth, no reintentar
        console.log('Error no recuperable, no se reintentará:', errorInfo.type);
        throw error;
      }
    }
  }
  
  // Esta línea nunca debería ejecutarse, pero por seguridad
  throw lastError;
};

// Función especializada para reintentos de registro
export const retryRegistration = async (requestFunction, maxRetries = 2, initialDelay = 3000) => {
  console.log('🔄 Iniciando proceso de registro con reintentos inteligentes...');
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`📝 Intento de registro ${attempt} de ${maxRetries}...`);
      const result = await requestFunction();
      console.log('✅ Registro exitoso!');
      return result;
    } catch (error) {
      const errorInfo = handleApiError(error);
      console.log(`❌ Intento ${attempt} falló:`, errorInfo.message);
      
      // Si es el último intento o es un error no recuperable, lanzar el error
      if (attempt === maxRetries || !shouldRetryRegistration(errorInfo)) {
        if (errorInfo.type === 'timeout') {
          error.message = `El registro está tardando más de ${maxRetries * 2} minutos. Esto puede deberse a una alta carga en el servidor. Por favor, verifica tu conexión a internet y vuelve a intentar en unos minutos. Si el problema persiste, contacta al soporte técnico.`;
        }
        throw error;
      }
      
      // Esperar más tiempo entre intentos para registros
      const nextDelay = initialDelay * attempt;
      console.log(`⏳ Esperando ${nextDelay / 1000}s antes del siguiente intento...`);
      await new Promise(resolve => setTimeout(resolve, nextDelay));
    }
  }
};

// Determinar si un error de registro debería reintentarse
const shouldRetryRegistration = (errorInfo) => {
  const retryableTypes = ['network', 'timeout', 'server'];
  return retryableTypes.includes(errorInfo.type);
};
